// Class-driven scroll reveals, ported from the Panorays theme (README, "Entrance animation").
const TYPES = [ 'fade', 'fade-up', 'fade-down', 'fade-left', 'fade-right', 'zoom-in', 'zoom-out' ];
const SELF_PREFIX = 'animate-';
const CHILDREN_PREFIX = 'animate-children-';
const SELF_SELECTOR = TYPES.map( ( type ) => `.${ SELF_PREFIX }${ type }` ).join( ', ' );
const CHILDREN_SELECTOR = TYPES.map( ( type ) => `.${ CHILDREN_PREFIX }${ type }` ).join( ', ' );
const STAGGER_RE = /^animate-stagger-(\d+)$/;
const DELAY_RE = /^animate-delay-(\d+)$/;
const DEFAULT_STAGGER = 100;
const READY_CLASS = 'anim-ready';
const PREPARED_CLASS = 'anim';
const VISIBLE_CLASS = 'is-visible';
const DELAY_VAR = '--anim-delay';
const READY_KEY = 'animateChildrenReady';

let observer = null;

const reducedMotion = () => window.matchMedia( '(prefers-reduced-motion: reduce)' ).matches;

const findType = ( el, prefix ) => TYPES.find( ( type ) => el.classList.contains( `${ prefix }${ type }` ) ) ?? null;

const readClassValue = ( el, regex, fallback ) => {
	for ( const cls of el.classList ) {
		const match = cls.match( regex );
		if ( match ) {
			return parseInt( match[ 1 ], 10 );
		}
	}
	return fallback;
};

const setDelay = ( el, delay ) => {
	if ( delay > 0 ) {
		el.style.setProperty( DELAY_VAR, `${ delay }ms` );
	}
};

const reveal = ( el ) => el.classList.add( VISIBLE_CLASS );

function expandChildren( root ) {
	root.querySelectorAll( CHILDREN_SELECTOR ).forEach( ( group ) => {
		if ( group.dataset[ READY_KEY ] ) {
			return;
		}
		group.dataset[ READY_KEY ] = 'true';

		const type = findType( group, CHILDREN_PREFIX );
		const stagger = readClassValue( group, STAGGER_RE, DEFAULT_STAGGER );
		const baseDelay = readClassValue( group, DELAY_RE, 0 );

		[ ...group.children ].forEach( ( child, index ) => {
			if ( findType( child, SELF_PREFIX ) ) {
				return;
			}
			child.classList.add( `${ SELF_PREFIX }${ type }` );
			setDelay( child, baseDelay + index * stagger );
		} );
	} );
}

function getObserver() {
	if ( ! observer ) {
		observer = new IntersectionObserver( ( entries ) => {
			entries.forEach( ( entry ) => {
				if ( entry.isIntersecting ) {
					const el = entry.target;
					// Hand the element its own transitions back (e.g. button hovers) once revealed.
					const done = ( event ) => {
						if ( event.target === el && event.propertyName === 'opacity' ) {
							el.classList.remove( PREPARED_CLASS );
							el.removeEventListener( 'transitionend', done );
						}
					};
					el.addEventListener( 'transitionend', done );
					reveal( el );
					observer.unobserve( el );
				}
			} );
		} );
	}
	return observer;
}

const EntranceAnimation = {
	// Runs at script load, before DOMContentLoaded, so the hidden state never flashes.
	markReady() {
		if ( ! reducedMotion() && 'IntersectionObserver' in window ) {
			document.documentElement.classList.add( READY_CLASS );
		}
	},

	// Safe to call again after injecting markup.
	init( root = document ) {
		if ( ! document.documentElement.classList.contains( READY_CLASS ) ) {
			root.querySelectorAll( `${ SELF_SELECTOR }, ${ CHILDREN_SELECTOR }` ).forEach( reveal );
			return;
		}

		expandChildren( root );

		root.querySelectorAll( SELF_SELECTOR ).forEach( ( el ) => {
			if ( el.classList.contains( PREPARED_CLASS ) ) {
				return;
			}
			el.classList.add( PREPARED_CLASS );
			if ( ! el.style.getPropertyValue( DELAY_VAR ) ) {
				setDelay( el, readClassValue( el, DELAY_RE, 0 ) );
			}
			getObserver().observe( el );
		} );
	},
};

export default EntranceAnimation;
